import { type MouseEvent as ReactMouseEvent } from "react";
import {
  AnchoredPopup,
  CarbonsixIcon,
  ChevronIcon,
  GithubIcon,
  HatcheryIcon,
  LogOutIcon,
  Text,
  useAnchoredPopupAnchor,
} from "@carbon6/c6-frontend-design";
import { appTextModes } from "../theme";
import styles from "./SidebarAccountMenu.module.css";

type SidebarAccountMenuProps = {
  // 접힘 레일에선 trigger 가 브랜드 마크(맨 아래)로, 펼침에선 타이틀 옆 chevron 으로 렌더한다.
  collapsed: boolean;
  userName: string;
  // GitHub 핸들. provider 가 주지 않으면 null 이라 표시하지 않는다.
  username: string | null;
  // Intake `/v1/me` 가 준 조직명. 아직 확인하지 못했으면(BE 불통) null 이라 기본 라벨을 보인다.
  organizationName: string | null;
  onLogout: () => void;
};

const fallbackOrganizationLabel = "carbon6robotics";

// 브랜드 옆 chevron 트리거 + 계정 팝업. 상단에 Carbonsix 조직 헤더, 그 아래 계정 행(GitHub 마크·이름·
// 핸들), 로그아웃을 담는다. 데이터·핸들러는 PageBase 가 주입한다.
export function SidebarAccountMenu({
  collapsed,
  userName,
  username,
  organizationName,
  onLogout,
}: SidebarAccountMenuProps) {
  const anchor = useAnchoredPopupAnchor();

  function handleTriggerClick(event: ReactMouseEvent<HTMLButtonElement>) {
    if (anchor.open) {
      anchor.close();
      return;
    }
    anchor.openFromElement(event);
  }

  function handleLogoutClick() {
    anchor.close();
    onLogout();
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="계정 메뉴"
        aria-haspopup="dialog"
        aria-expanded={anchor.open}
        data-open={anchor.open ? "true" : "false"}
        // 열린 상태에서 트리거를 다시 눌렀을 때, pointerdown 이 document 까지 전파되면 AnchoredPopup 의
        // 바깥-클릭 닫힘이 먼저 닫고(open=false) 이어지는 click 이 다시 열어 토글이 깨진다. 전파를 끊어
        // click 만으로 깔끔히 토글한다(c6 useTriggerToggle 과 동일 패턴).
        onPointerDown={(event) => event.stopPropagation()}
        onClick={handleTriggerClick}
      >
        {collapsed ? (
          <HatcheryIcon className={styles.triggerLogo} />
        ) : (
          <ChevronIcon className={styles.triggerIcon} />
        )}
      </button>

      <AnchoredPopup
        open={anchor.open}
        anchor={anchor.anchor}
        onClose={anchor.close}
        placement={collapsed ? "right-end" : "bottom"}
        width="17rem"
        role="dialog"
        aria-label="계정 메뉴"
        // 팝업 배경을 좌측 내비(사이드바)와 동일한 surface 톤으로 맞춘다. c6 Panel 기본은
        // surfaceRaised 라, style 로 덮어써 styled-component 기본값보다 우선하게 한다.
        style={{ backgroundColor: "var(--c6-color-bg-surface)" }}
      >
        <AnchoredPopup.Body padding="none">
          <div className={styles.brand}>
            <CarbonsixIcon size={32} className={styles.brandIcon} />
            <div className={styles.brandText}>
              <Text variant={appTextModes["body-bold"]} className={styles.brandName}>
                {organizationName ?? fallbackOrganizationLabel}
              </Text>
            </div>
          </div>

          <div className={styles.accountRow}>
            <GithubIcon className={styles.accountIcon} />
            <Text variant={appTextModes["body-bold"]} className={styles.accountName}>
              {userName}
            </Text>
            {username ? <span className={styles.accountHandle}>{`@${username}`}</span> : null}
          </div>
        </AnchoredPopup.Body>

        <AnchoredPopup.Footer divider={false} className={styles.footer}>
          <button type="button" className={styles.logoutButton} onClick={handleLogoutClick}>
            <LogOutIcon className={styles.logoutIcon} />
            <span className={styles.logoutText}>로그아웃</span>
          </button>
        </AnchoredPopup.Footer>
      </AnchoredPopup>
    </>
  );
}
